/**
 * Severity calculation utilities for discipline service
 */

import { DISCIPLINE_TYPES } from '../constants';
import { getOffenceField } from './fieldNormalizer';

/**
 * Derive severity from a numeric level
 * @param {number} level - The level (1-5)
 * @returns {string} Severity level (low, medium, high, critical)
 */
export function deriveSeverityFromLevel(level) {
  // Current mapping - can be adjusted as needed
  if (level >= 4) return 'critical';
  if (level >= 3) return 'high';
  if (level >= 2) return 'medium';
  return 'low';
}

/**
 * Calculate severity for a rule-triggered consequence
 * @param {Object} rule - The discipline rule
 * @param {Array} triggeringMentions - The mentions that triggered the rule
 * @returns {string} Severity level
 */
export function calculateSeverity(rule, triggeringMentions) {
  const mentionCount = triggeringMentions.length;

  if (mentionCount >= 5 || rule.actions?.accountability) return 'high';
  if (mentionCount >= 3) return 'medium';
  return 'low';
}

/**
 * Calculate severity for mention rule consequences
 * @param {Object} rule - The mention rule
 * @returns {string} Severity level
 */
export function calculateMentionRuleSeverity(rule) {
  if (rule.consequenceType === 'suspension') return 'high';
  if (rule.consequenceType === 'saturday-detention') return 'high';
  if (rule.consequenceType === 'detention') return 'medium';
  return 'low';
}

/**
 * Calculate severity for accountability consequences
 * @param {Object} threshold - The accountability threshold
 * @returns {string} Severity level
 */
export function calculateAccountabilitySeverity(threshold) {
  const type = threshold.consequenceType;
  if (type === 'suspension' || type === 'saturday-detention') return 'high';
  if (type === 'detention') return 'medium';
  return 'low';
}

/**
 * Infer discipline type from document data
 * Used for migrating documents with generic 'consequence' type
 * @param {Object} data - Document data
 * @returns {string} Inferred discipline type or 'requires-manual-review'
 */
export function inferDisciplineType(data) {
  // Check for explicit type markers in the data structure

  // 1. Check for demerit-specific fields
  if (data.points !== undefined && data.points !== null) {
    return DISCIPLINE_TYPES.DEMERIT;
  }

  // 2. Check for detention-specific fields
  if (data.location || data.scheduledDate || data.detentionLocation) {
    return DISCIPLINE_TYPES.DETENTION;
  }

  // 3. Check for suspension-specific fields
  if (data.startDate && data.endDate && !data.location) {
    return DISCIPLINE_TYPES.SUSPENSION;
  }

  // 4. Check for saturday detention markers
  if (data.consequenceType === 'saturday-detention' ||
      (data.scheduledDate && typeof data.scheduledDate === 'string' &&
       new Date(data.scheduledDate).getDay() === 6)) {
    return DISCIPLINE_TYPES.SATURDAY_DETENTION;
  }

  // 5. Check if triggered by a rule - try to infer from rule data
  if (data.triggeredByRule) {
    // Common patterns from rule-triggered consequences
    if (data.consequenceType) {
      // Map consequenceType values to our standard types
      const typeMapping = {
        'detention': DISCIPLINE_TYPES.DETENTION,
        'demerit': DISCIPLINE_TYPES.DEMERIT,
        'demerits': DISCIPLINE_TYPES.DEMERIT,
        'suspension': DISCIPLINE_TYPES.SUSPENSION,
        'warning': DISCIPLINE_TYPES.WARNING,
        'saturday-detention': DISCIPLINE_TYPES.SATURDAY_DETENTION
      };

      const mappedType = typeMapping[data.consequenceType.toLowerCase()];
      if (mappedType) {
        return mappedType;
      }
    }

    // Check for accountability rule patterns
    if (data.accountabilityThreshold) {
      // Accountability rules typically create detentions
      return DISCIPLINE_TYPES.DETENTION;
    }
  }

  // 6. Check for warning indicators
  if (data.severity === 'warning' || data.consequenceType === 'warning') {
    return DISCIPLINE_TYPES.WARNING;
  }

  // 7. Try to infer from text fields
  const textToCheck = getOffenceField(data).toLowerCase();
  if (textToCheck.includes('detention')) {
    return DISCIPLINE_TYPES.DETENTION;
  }
  if (textToCheck.includes('demerit') || textToCheck.includes('points')) {
    return DISCIPLINE_TYPES.DEMERIT;
  }
  if (textToCheck.includes('suspension') || textToCheck.includes('suspend')) {
    return DISCIPLINE_TYPES.SUSPENSION;
  }

  // 8. Default based on severity if available
  if (data.severity) {
    const severityDefaults = {
      'low': DISCIPLINE_TYPES.WARNING,
      'medium': DISCIPLINE_TYPES.DEMERIT,
      'high': DISCIPLINE_TYPES.DETENTION,
      'critical': DISCIPLINE_TYPES.SUSPENSION
    };

    const defaultType = severityDefaults[data.severity.toLowerCase()];
    if (defaultType) {
      console.warn(`Inferring type from severity: ${data.severity} → ${defaultType}`);
      return defaultType;
    }
  }

  // Cannot determine type - requires manual review
  console.error('Cannot infer discipline type for document:', {
    id: data.id,
    type: data.type,
    consequenceType: data.consequenceType,
    severity: data.severity,
    offence: getOffenceField(data)
  });

  return 'requires-manual-review';
}